/**
 * Wrapper on top of `window.localStorage` which makes access easier.
 *
 * Should not be used for storing large amount of data.
 *
 * The key this class uses in the localStorage is `"manager-${this.id}"`
 *
 * Uses object dot notation to access or save data. Example: `foo.bar.lorem`
 * @class LocalStorageManager
 */
class LocalStorageManager {
  /**
   * Creates an instance of LocalStorageManager.
   * @param {number} [id=0] id of the LocalStorageManager
   */
  constructor(id = 0) {
    this.id = id;

    this._data = {};
    this._length = 0;

    this._initialized = false;
  }

  /**
   * Number of keys in the `_data` object
   * @readonly
   */
  get length() {
    return this._length;
  }

  /**
   * Saves the `_data` to the `window.localStorage`
   */
  _save() {
    window.localStorage.setItem('manager-' + this.id, JSON.stringify(this._data));
  }

  /**
   * Saves the data from `window.localStorage` to `_data` object
   */
  _restore() {
    this._data = JSON.parse(window.localStorage.getItem('manager-' + this.id));

    if (!this._data) {
      this._data = {};
    }
  }

  /**
   * Gets an item from the data stored in `window.localStorage`
   * @param {String} query
   * @returns {*} item
   */
  getItem(query) {
    let data = this._data;

    query = query.split('.');
    for (let i = 0; i < query.length; i++) {
      data = data[query[i]];
    }

    return data;
  }

  /**
   * Sets the value of the key specified in `query`. Creates the missing objects
   * @param {String} query
   * @param {*} value
   */
  setItem(query, value) {
    let data = this._data;

    query = query.split('.');
    for (let i = 0; i < query.length; i++) {
      if (!data.hasOwnProperty(query[i])) {
        data[query[i]] = {};
      }

      if (i < query.length - 1) {
        data = data[query[i]];
      }
    }

    data[query[query.length - 1]] = value;

    this._save();
  }

  /**
   * Wipes all data
   */
  clear() {
    this._data = {};
    this._save();
  }

  /**
   * Initializes the `LocalStorageManager`. Reads all data
   */
  init() {
    let success = false;

    if (!this._initialized) {
      try {
        this._restore();
        success = true;
      } catch (err) {
        console.log(err);
      } finally {
        this._initialized = true;
      }
    }

    return success;
  }
}

export default LocalStorageManager;
